<template>
	<div class="pui-form">
		<portcallinvoices-modals :modelName="modelName"></portcallinvoices-modals>
		<v-form ref="form" v-model="valid" lazy-validation @submit.prevent v-if="modelLoaded">
			<v-layout wrap class="pui-form-layout">
				<v-flex xs12 md12 lg12>
					<pui-field-set :title="$t('portcallinvoices.fieldset.main')">
						<v-row dense>
							<!-- Invoice Type -->
							<v-flex xs12 md12 lg6 xl6>
								<pui-select
									:id="`portcallinvoices-invoicetypeid`"
									attach="portcallinvoices-invoicetypeid"
									:label="$t('portcallinvoices.invoicetype')"
									toplabel
									v-model="model"
									modelName="vlupminvoicetype"
									:disabled="!isCreatingElement"
									reactive
									:modelFormMapping="{ invoicetypeid: 'invoicetypeid' }"
									:itemsToSelect="itemsToSelectInvoicetype"
									:itemValue="['invoicetypeid']"
									:itemText="(item) => `${item.typedescription}`"
								></pui-select>
							</v-flex>

							<!-- Status (only when editing) -->
							<v-flex xs12 md12 lg6 xl6 v-if="!isCreatingElement">
								<pui-select
									:id="`portcallinvoices-invoicestatusid`"
									attach="portcallinvoices-invoicestatusid"
									:label="$t('portcallinvoices.status')"
									toplabel
									v-model="model"
									modelName="vlupminvoicestatus"
									:disabled="!isCreatingElement"
									reactive
									:modelFormMapping="{ invoicestatusid: 'invoicestatusid' }"
									:itemsToSelect="itemsToSelectInvoicestatus"
									:itemValue="['invoicestatusid']"
									:itemText="(item) => `${item.statusdescription}`"
								></pui-select>
							</v-flex>
						</v-row>

						<v-row dense>
							<!-- Description -->
							<v-flex xs12 md12 lg12 xl12>
								<pui-text-area
									:id="`description-portcallinvoices`"
									v-model="model.description"
									:label="$t('portcallinvoices.description')"
									:disabled="this.model.invoicestatusid != null && this.model.invoicestatusid != 1"
									toplabel
									rows="3"
									maxlength="500"
								></pui-text-area>
							</v-flex>
						</v-row>
						<v-row dense v-if="model.incorrectcomments">
							<!-- Incorrect comments (when marked as incorrect) -->
							<v-flex xs12 md12 lg12 xl12>
								<pui-text-area
									:id="`incorrectcomments-portcallinvoices`"
									v-model="model.incorrectcomments"
									:label="$t('portcallinvoices.incorrectcomments')"
									disabled
									toplabel
									rows="3"
								></pui-text-area>
							</v-flex>
						</v-row>
					</pui-field-set>

					<!-- Receipt Upload Section (only when editing and status allows) -->
					<pui-field-set :title="$t('portcallinvoices.fieldset.receipt')" v-if="!isCreatingElement">
						<v-row dense>
							<!-- Receipt file name -->
							<v-flex xs12 md12 lg6 xl6>
								<pui-text-field
									:label="$t('portcallinvoices.receiptfilename')"
									v-model="displayReceiptFilename"
									toplabel
									disabled
								></pui-text-field>
								<input type="file" v-show="false" ref="receiptDocument" :accept="fileExtensions" @change="onReceiptFilePicked" />
							</v-flex>
							<v-flex xs12 md12 lg6 xl6>
								<!-- Upload button (only when no receipt or status allows re-upload) -->
								<v-btn class="header_info_btn primary" style="margin-top: 20px" v-if="canUploadReceipt" @click="pickReceiptFile">
									{{ $t('portcallinvoices.selectreceipt') }}
								</v-btn>
								<!-- View/Download buttons (when receipt exists) -->
								<v-btn class="header_info_btn primary ml-1 mt-5" v-if="model.receiptdocumentid" @click="viewReceipt">
									{{ $t('portcallinvoices.viewreceipt') }}
								</v-btn>
								<v-btn class="header_info_btn primary ml-1 mt-5" v-if="model.receiptdocumentid" @click="downloadReceipt">
									{{ $t('portcallinvoices.downloadreceipt') }}
								</v-btn>
								<v-btn class="header_info_btn error ml-1 mt-5" v-if="model.receiptdocumentid && hasReceiptWritePermission" @click="deleteReceipt"> Delete </v-btn>
							</v-flex>
						</v-row>

						<!-- Drag and drop zone -->
						<v-row dense v-if="!isMobile && canUploadReceipt">
							<v-flex xs12 md12 lg6 xl6>
								<div
									class="drop align-center"
									@dragover.prevent
									@dragenter="onDragEnter"
									@dragleave="onDragLeave"
									@drop="onReceiptDrop"
									@click="pickReceiptFile"
								>
									<div class="helper"></div>
									<label v-if="!selectedReceiptFile" class="label-file">{{ $t('portcallinvoices.dropreciepthere') }}</label>
									<i v-if="selectedReceiptFile" :class="selectedFileType" style="font-size: 30px"></i>
									<label v-if="selectedReceiptFile" class="label-file">{{ selectedReceiptFile.name }}</label>
								</div>
							</v-flex>
						</v-row>

						<!-- Receipt upload date -->
						<v-row dense v-if="model.receiptuploaddate">
							<v-flex xs12 md12 lg6 xl6>
								<pui-date-field
									:id="`receiptuploaddate-portcallinvoices`"
									v-model="model.receiptuploaddate"
									:label="$t('portcallinvoices.receiptuploaddate')"
									disabled
									toplabel
									time
								></pui-date-field>
							</v-flex>
						</v-row>
					</pui-field-set>
				</v-flex>
			</v-layout>

			<!-- Page Footer -->
			<pui-form-footer class="mb-4 pb-4">
				<pui-form-footer-btns
					:formDisabled="formDisabled"
					:saveDisabled="saving"
					:saveAndNew="saveAndNew"
					:saveAndUpdate="saveAndUpdate"
					:save="save"
					:back="back"
				></pui-form-footer-btns>
			</pui-form-footer>
		</v-form>
		<pui-form-loading v-else></pui-form-loading>

		<!-- Empty file selected dialog -->
		<pui-modal-dialog
			v-if="showEmptyFileDialog"
			:okText="$t('pui9.accept')"
			:titleText="$t('form.puidocument.error.emptyfileTitle')"
			:messageText="$t('form.puidocument.error.emptyfile')"
			:dialogName="`emptyFileDialog-${this.modelName}`"
		></pui-modal-dialog>
	</div>
</template>

<script>
import PuiFormMethodsMixin from '@Pui9Mixins/PuiFormMethodsMixin';
import portcallinvoicesModals from './PortcallinvoicesModals.vue';

// Invoice Status IDs
const STATUS_CREATED = 1;
const STATUS_INCORRECT = 3;

export default {
	name: 'portcallinvoices-form',
	mixins: [PuiFormMethodsMixin],
	components: {
		'portcallinvoices-modals': portcallinvoicesModals
	},
	data() {
		return {
			modelName: 'portcallinvoices',
			parentModelName: 'portcalls',
			selectedReceiptFile: null,
			selectedFileType: null,
			showEmptyFileDialog: false,
			fileExtensions: '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx',
			fileTypes: {
				'image/png': 'fas fa-file-image',
				'image/jpeg': 'fas fa-file-image',
				'application/pdf': 'fas fa-file-pdf',
				'application/vnd.ms-excel': 'fas fa-file-excel',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fas fa-file-excel',
				'application/msword': 'fas fa-file-word',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'fas fa-file-word'
			}
		};
	},
	methods: {
		afterGetData() {
			if (this.$router.currentRoute.path.startsWith('/portcalls/')) {
				localStorage.setItem('tabFromAction', 'portcallinvoices');
			}

			// Try to get portcallid from parentpk param
			if (this.$route.params.parentpk) {
				this.model.portcallid = JSON.parse(atob(this.$route.params.parentpk)).portcallid;
			} else {
				// Extract portcallid from URL path: /portcalls/update/{pk}/portcallinvoices/...
				const pathParts = this.$route.path.split('/');
				const portcallsIndex = pathParts.indexOf('portcalls');
				if (portcallsIndex !== -1 && pathParts[portcallsIndex + 2]) {
					const parentPkEncoded = pathParts[portcallsIndex + 2];
					try {
						this.model.portcallid = JSON.parse(atob(parentPkEncoded)).portcallid;
					} catch (e) {
						console.error('Error parsing portcallid from URL:', e);
					}
				}
			}

			// If there's a receipt document, fetch its filename from pui_document
			if (this.model.receiptdocumentid) {
				this.fetchReceiptFilename();
			}

			console.log('model: ', this.model);

			// No need to set default values - backend will calculate everything
			// based on portcallid and invoicetypeid in beforeInsert
		},
		fetchReceiptFilename() {
			if (!this.model.receiptdocumentid) {
				return;
			}
			const url = '/portcallinvoices/getReceiptFilename';
			const params = { documentid: this.model.receiptdocumentid };
			this.$puiRequests.getRequest(
				url,
				params,
				(response) => {
					if (response.data) {
						this.$set(this.model, 'receiptfilename', response.data);
					}
				},
				(error) => {
					console.error('Error fetching receipt filename:', error);
				}
			);
		},
		pickReceiptFile() {
			this.$refs.receiptDocument.click();
		},
		onReceiptFilePicked(e) {
			const files = e.target.files;
			if (files[0] !== undefined) {
				this.onReceiptFileDropped(files[0]);
			} else {
				this.clearSelectedReceipt();
			}
		},
		onDragEnter(e) {
			if (e.target.className === 'drop align-center') {
				e.target.style.border = '2px dashed #166bf2';
			}
		},
		onDragLeave(e) {
			if (e.target.className === 'drop align-center') {
				e.target.style.border = '2px dashed var(--N-100)';
			}
		},
		onReceiptDrop(e) {
			e.stopPropagation();
			e.preventDefault();
			e.target.style.border = '2px dashed var(--N-100)';
			const files = e.dataTransfer.files;
			if (files[0]) {
				this.onReceiptFileDropped(files[0]);
			}
		},
		onReceiptFileDropped(file) {
			if (!file || file.size <= 0) {
				this.showEmptyFileDialog = true;
				this.clearSelectedReceipt();
				return;
			}

			if (file.name.lastIndexOf('.') <= 0) {
				return;
			}

			if (!this.validFileExtension(file)) {
				this.$puiNotify.error(this.$t('form.puidocument.error.fileextension.notallowed'));
				return;
			}

			const self = this;
			const fr = new window.FileReader();
			fr.addEventListener('load', () => {
				self.selectedReceiptFile = file;
				self.selectedFileType = self.fileTypes[file.type] || 'fas fa-file';
			});
			fr.readAsDataURL(file);
		},
		validFileExtension(file) {
			if (this.fileExtensions === null) {
				return true;
			}
			const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
			return this.fileExtensions.includes(fileExtension);
		},
		clearSelectedReceipt() {
			this.selectedReceiptFile = null;
			this.selectedFileType = null;
			if (this.$refs.receiptDocument) {
				this.$refs.receiptDocument.value = '';
			}
		},
		// Override update method to handle receipt upload + normal update
		update(shouldStayInPage) {
			// CASO 1: hay fichero de recibo seleccionado
			if (this.selectedReceiptFile && this.model.invoiceid) {
				if (!this.selectedReceiptFile.size || this.selectedReceiptFile.size <= 0) {
					this.showEmptyFileDialog = true;
					this.clearSelectedReceipt();
					return;
				}

				this.saving = true;

				const uploadedFilename = this.selectedReceiptFile.name;
				const formData = new window.FormData();
				formData.append('file', this.selectedReceiptFile, this.selectedReceiptFile.name);

				// 1) Primero guardamos los cambios normales (description, etc.)
				this.$puiRequests.putRequest(
					'/portcallinvoices/update',
					this.model,
					(updateResponse) => {
						if (updateResponse.status === 200 && updateResponse.data) {
							// Sincronizamos el modelo con lo que devuelve el back
							Object.assign(this.model, updateResponse.data);

							// 2) Después subimos el recibo
							this.$puiRequests.uploadFileRequest(
								'/portcallinvoices/uploadReceipt',
								formData,
								(uploadResponse) => {
									if (uploadResponse.status === 200 && uploadResponse.data) {
										// El back devuelve la factura ya actualizada (PAID, doc, fecha…)
										Object.assign(this.model, uploadResponse.data);
										this.$set(this.model, 'receiptfilename', uploadedFilename);

										this.$puiNotify.success(this.$t('portcallinvoices.receiptuploadsuccess'));
										this.clearSelectedReceipt();

										// Refrescamos la fila del grid
										this.$puiEvents.$emit(`onPui-updateRow-dataTable-${this.modelDependency.name}`, uploadResponse.data);

										// Si es "Save and back", volvemos al grid
										if (!(shouldStayInPage === true)) {
											this.back();
										}
									} else {
										this.onError(uploadResponse);
									}

									this.saving = false;
									this.resetAfterSaveActions && this.resetAfterSaveActions();
								},
								(error) => {
									this.saving = false;
									this.resetAfterSaveActions && this.resetAfterSaveActions();
									this.onSaveError && this.onSaveError();
									this.onError(error);
								},
								{ invoiceid: this.model.invoiceid }
							);
						} else {
							this.saving = false;
							this.onError(updateResponse);
							this.resetAfterSaveActions && this.resetAfterSaveActions();
						}
					},
					(error) => {
						this.saving = false;
						this.resetAfterSaveActions && this.resetAfterSaveActions();
						this.onSaveError && this.onSaveError();
						this.onError(error);
					}
				);

				// CASO 2: no hay fichero → update normal
			} else {
				this.saving = true;

				this.$puiRequests.putRequest(
					'/portcallinvoices/update',
					this.model,
					(response) => {
						this.saving = false;
						if (response.status === 200 && response.data) {
							Object.assign(this.model, response.data);

							this.$puiEvents.$emit(`onPui-updateRow-dataTable-${this.modelDependency.name}`, response.data);

							if (!(shouldStayInPage === true)) {
								this.back();
							}
						} else {
							this.onError(response);
						}
						this.resetAfterSaveActions && this.resetAfterSaveActions();
					},
					(error) => {
						this.saving = false;
						this.resetAfterSaveActions && this.resetAfterSaveActions();
						this.onSaveError && this.onSaveError();
						this.onError(error);
					}
				);
			}
		},
		viewReceipt() {
			if (!this.model.receiptdocumentid) {
				return;
			}
			const url = `/portcallinvoices/viewReceipt`;
			const params = { documentid: this.model.receiptdocumentid };

			this.$puiRequests.downloadFileRequest(
				'get',
				url,
				params,
				(response) => {
					// Try to get filename from Content-Disposition header
					let filename = 'receipt.pdf';
					const contentDisposition = response.headers['content-disposition'];
					if (contentDisposition) {
						const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
						if (filenameMatch && filenameMatch[1]) {
							filename = filenameMatch[1].replace(/['"]/g, '');
						}
					}
					// Fallback to model receiptfilename
					if (filename === 'receipt.pdf' && this.model.receiptfilename) {
						filename = this.model.receiptfilename;
					}
					// Determine content type from file extension
					const extension = filename.split('.').pop().toLowerCase();
					let type = 'application/octet-stream';
					switch (extension) {
						case 'pdf':
							type = 'application/pdf';
							break;
						case 'png':
							type = 'image/png';
							break;
						case 'jpg':
						case 'jpeg':
							type = 'image/jpeg';
							break;
						case 'txt':
							type = 'text/plain';
							break;
						case 'doc':
							type = 'application/msword';
							break;
						case 'docx':
							type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
							break;
						case 'xls':
							type = 'application/vnd.ms-excel';
							break;
						case 'xlsx':
							type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
							break;
					}
					// Open file in new tab with correct content type
					window.open(window.URL.createObjectURL(new Blob([response.data], { type: type })), '_blank');
				},
				(error) => {
					console.error('Error viewing receipt:', error);
					this.$puiNotify.error(this.$t('portcallinvoices.receiptviewerror'));
				}
			);
		},
		downloadReceipt() {
			if (!this.model.receiptdocumentid) {
				return;
			}
			const url = `/portcallinvoices/downloadReceipt`;
			const params = { documentid: this.model.receiptdocumentid };

			this.$puiRequests.downloadFileRequest(
				'get',
				url,
				params,
				(response) => {
					// Try to get filename from Content-Disposition header
					let filename = 'receipt';
					const contentDisposition = response.headers['content-disposition'];
					if (contentDisposition) {
						const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
						if (filenameMatch && filenameMatch[1]) {
							filename = filenameMatch[1].replace(/['"]/g, '');
						}
					}
					// Fallback to model receiptfilename
					if (filename === 'receipt' && this.model.receiptfilename) {
						filename = this.model.receiptfilename;
					}
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(new window.Blob([response.data]));
					link.download = filename;
					link.click();
				},
				(error) => {
					console.error('Error downloading receipt:', error);
					this.$puiNotify.error(this.$t('portcallinvoices.receiptdownloaderror'));
				}
			);
		},
		deleteReceipt() {
			if (!this.hasReceiptWritePermission) return;
			if (!this.model.invoiceid || !this.model.receiptdocumentid) return;
			this.saving = true;
			this.$puiRequests.postRequest(
				`/portcallinvoices/deletePaymentReceipt?invoiceId=${this.model.invoiceid}`,
				null,
				(response) => {
					this.saving = false;
					if (response.status === 200 && response.data) {
						// Limpiar campos de recibo del modelo para evitar buscar un documento inexistente
						console.log('Deleting receipt filename:', this.model.receiptfilename);

						this.model.receiptfilename = '';

						console.log('data: ', response.data);

						// Recargar el modelo completo para refrescar el estado y los datos
						this.afterGetData();
						this.$puiNotify.success(this.$t('action.portcallinvoices.deleteReceiptsucccess'));
						this.back();
					} else {
						this.onError(response);
					}
				},
				(error) => {
					this.saving = false;
					this.onError(error);
				}
			);
		}
	},
	computed: {
		itemsToSelectInvoicetype() {
			return [{ invoicetypeid: this.model.invoicetypeid }];
		},
		itemsToSelectInvoicestatus() {
			return [{ invoicestatusid: this.model.invoicestatusid }];
		},
		isMobile() {
			return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
		},
		hasReceiptWritePermission() {
			const functionalities = this.$store.getters.getSession.functionalities || [];
			return (
				functionalities.includes('WRITE_PORTCALLINVOICES_RECEIPT') ||
				functionalities.includes('WRITE_PORTCALLINVOICES')
			);
		},
		canUploadReceipt() {
			if (!this.hasReceiptWritePermission) {
				return false;
			}
			// Can upload when status is CREATED or INCORRECT
			// Handle both number and string types
			const status = parseInt(this.model.invoicestatusid);
			console.log('canUploadReceipt - status:', status, 'model.invoicestatusid:', this.model.invoicestatusid);
			return status === STATUS_CREATED || status === STATUS_INCORRECT || isNaN(status);
		},
		displayReceiptFilename() {
			// Shows the filename in the text field - either from selected file or from existing receipt
			if (this.selectedReceiptFile) {
				return this.selectedReceiptFile.name;
			}
			if (this.model.receiptfilename) {
				return this.model.receiptfilename;
			}
			if (this.model.receiptdocumentid) {
				return this.$t('portcallinvoices.receiptattached');
			}
			return '';
		}
	},
	mounted() {
		this.$puiEvents.$on(`pui-modalDialog-emptyFileDialog-${this.modelName}-ok`, () => {
			this.showEmptyFileDialog = false;
		});
	},
	destroyed() {
		this.$puiEvents.$off(`pui-modalDialog-emptyFileDialog-${this.modelName}-ok`);
	}
};
</script>

<style scoped>
.align-center {
	text-align: center;
}
.helper {
	height: 100%;
	display: inline-block;
	vertical-align: middle;
	width: 0;
}
.label-file {
	color: var(--primarycolor);
	font-weight: bold;
	font-size: 18px;
}
.drop {
	background-color: var(--N-10);
	border-radius: 4px;
	border: 2px dashed var(--N-100);
	height: 100%;
	width: 100%;
	max-height: 200px;
	cursor: pointer;
}
#selectFile {
	color: white !important;
	margin-left: 2%;
	margin-bottom: 3%;
}
</style>
